import { useEffect, useRef, useState, KeyboardEvent } from "react";
import { toast } from "sonner";
import { FileText, Image, Loader2, Mic, Paperclip, Send, Video, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getWhatsAppErrorMessage, WhatsAppMediaFile, WhatsAppMediaKind } from "@/services/whatsapp";

interface Attachment {
  kind: WhatsAppMediaKind;
  file: File;
  previewUrl: string | null;
}

const ATTACH_OPTIONS: { kind: WhatsAppMediaKind; label: string; accept: string; icon: typeof Image }[] = [
  { kind: "image", label: "Imagen", accept: "image/*", icon: Image },
  { kind: "video", label: "Video", accept: "video/*", icon: Video },
  { kind: "audio", label: "Audio", accept: "audio/*", icon: Mic },
  { kind: "file", label: "Documento", accept: "*/*", icon: FileText },
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ChatComposerProps {
  disabled?: boolean;
  onSendText: (text: string) => Promise<void>;
  onSendMedia: (kind: WhatsAppMediaKind, file: WhatsAppMediaFile, caption?: string) => Promise<void>;
}

export function ChatComposer({ disabled, onSendText, onSendMedia }: ChatComposerProps) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pickerKindRef = useRef<WhatsAppMediaKind>("file");
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    };
  }, []);

  const hasAttachments = attachments.length > 0;
  // Las notas de voz no admiten texto
  const captionDisabled = hasAttachments && attachments.every((a) => a.kind === "audio");
  const canSend = !disabled && !sending && (hasAttachments || text.trim().length > 0);

  const openPicker = (kind: WhatsAppMediaKind, accept: string) => {
    if (!fileInputRef.current) return;
    pickerKindRef.current = kind;
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  };

  const handleFilesChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    const kind = pickerKindRef.current;
    setAttachments((prev) => [
      ...prev,
      ...files.map((file) => ({
        kind,
        file,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      if (hasAttachments) {
        const total = attachments.length;
        for (let i = 0; i < total; i++) {
          setProgress({ current: i + 1, total });
          const att = attachments[i];
          const data = await fileToBase64(att.file);
          // El texto se envía como pie del primer adjunto
          const caption = i === 0 && !captionDisabled ? text.trim() || undefined : undefined;
          await onSendMedia(
            att.kind,
            { data, mimetype: att.file.type || "application/octet-stream", filename: att.file.name },
            caption,
          );
        }
        attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
        setAttachments([]);
      } else {
        await onSendText(text.trim());
      }
      setText("");
    } catch (e) {
      toast.error("No se pudo enviar el mensaje", {
        description: getWhatsAppErrorMessage(e, "Inténtalo nuevamente"),
      });
    } finally {
      setProgress(null);
      setSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border-t bg-background p-3 space-y-2">
      {hasAttachments && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att, i) => {
            const option = ATTACH_OPTIONS.find((o) => o.kind === att.kind)!;
            const Icon = option.icon;
            return (
              <div
                key={`${att.file.name}-${i}`}
                className="group relative flex items-center gap-2 rounded-lg border bg-muted/40 p-1.5 pr-7 max-w-[200px]"
              >
                {att.previewUrl ? (
                  <img src={att.previewUrl} alt="" className="h-9 w-9 rounded object-cover" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                )}
                <span className="truncate text-xs">{att.file.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  disabled={sending}
                  className="absolute right-1 top-1 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Quitar adjunto"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 text-muted-foreground"
              disabled={disabled || sending}
              aria-label="Adjuntar"
            >
              <Paperclip className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top">
            {ATTACH_OPTIONS.map(({ kind, label, accept, icon: Icon }) => (
              <DropdownMenuItem key={kind} onSelect={() => openPicker(kind, accept)}>
                <Icon className="mr-2 h-4 w-4" />
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilesChanged} />

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={disabled || captionDisabled}
          placeholder={
            captionDisabled
              ? "Las notas de voz no admiten texto"
              : hasAttachments
                ? "Añade un comentario..."
                : "Escribe un mensaje"
          }
          className="min-h-10 max-h-32 resize-none py-2.5"
        />

        <div className="flex flex-col items-center gap-0.5">
          <Button
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
            onClick={send}
            disabled={!canSend}
            aria-label="Enviar"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
          {progress && progress.total > 1 && (
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {progress.current}/{progress.total}
            </span>
          )}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground px-12">Enter para enviar · Shift + Enter para salto de línea</p>
    </div>
  );
}
